import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, TextField, Button, Box, Grid, Card, CardContent, Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import grupoMcpUsuarioService from '../../services/grupoMcpUsuarioService';
import GrupoMcpSearch from '../../components/GrupoMcpSearch';
import UsuarioSearch from '../../components/UsuarioSearch';
import type { GrupoMcp } from '../../services/grupoMcpService';

const GrupoMcpUsuarioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [grupoMcp, setGrupoMcp] = useState<number | null>(null);
  const [grupoMcpDescripcion, setGrupoMcpDescripcion] = useState('');
  const [usuario, setUsuario] = useState('');
  const [saving, setSaving] = useState(false);
  const [grupoSearchOpen, setGrupoSearchOpen] = useState(false);
  const [usuarioSearchOpen, setUsuarioSearchOpen] = useState(false);

  const handleGrupoSelect = (gm: GrupoMcp) => {
    setGrupoMcp(gm.grupoMcp);
    setGrupoMcpDescripcion(gm.descripcion || '');
    setGrupoSearchOpen(false);
  };

  const handleUsuarioSelect = (u: { usuario?: string; username?: string; id?: string }) => {
    setUsuario(u.usuario || u.username || u.id || '');
    setUsuarioSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grupoMcp || !usuario?.trim()) {
      enqueueSnackbar('Seleccione el grupo de caja y el usuario', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      await grupoMcpUsuarioService.create({ grupoMcp, usuario: usuario.trim() });
      enqueueSnackbar('Asignación creada exitosamente', { variant: 'success' });
      navigate('/sistema/grupo-caja-usuario');
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Error al crear la asignación';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            Nueva Asignación - Grupo de Caja por Usuario
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Grupo de Caja"
                    value={grupoMcpDescripcion || grupoMcp || ''}
                    fullWidth
                    required
                    disabled
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button variant="outlined" onClick={() => setGrupoSearchOpen(true)} sx={{ minWidth: 120 }}>
                    Buscar
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Usuario"
                    value={usuario}
                    fullWidth
                    required
                    disabled
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button variant="outlined" onClick={() => setUsuarioSearchOpen(true)} sx={{ minWidth: 120 }}>
                    Buscar
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Guardar
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/sistema/grupo-caja-usuario')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <GrupoMcpSearch open={grupoSearchOpen} onClose={() => setGrupoSearchOpen(false)} onSelect={handleGrupoSelect} />
      <UsuarioSearch open={usuarioSearchOpen} onClose={() => setUsuarioSearchOpen(false)} onSelect={handleUsuarioSelect} />
    </Box>
  );
};

export default GrupoMcpUsuarioForm;
